import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { useCreateOilType, useUpdateOilType } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { OilType, OilTypeFormData } from "~/types/purchasing";

interface OilTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  oilType?: OilType | null;
}

export function OilTypeDialog({
  open,
  onOpenChange,
  oilType,
}: OilTypeDialogProps) {
  const { toast } = useToast();
  const createMutation = useCreateOilType();
  const updateMutation = useUpdateOilType();

  const isEditing = !!oilType;

  const [formData, setFormData] = useState<OilTypeFormData>({
    name: "",
    code: "",
    description: "",
    density_kg_per_liter: 0.92,
    minimum_stock_liters: undefined,
  });

  useEffect(() => {
    if (oilType) {
      setFormData({
        name: oilType.name,
        code: oilType.code,
        description: oilType.description || "",
        density_kg_per_liter: oilType.density_kg_per_liter,
        minimum_stock_liters: oilType.minimum_stock_liters ?? undefined,
      });
    } else {
      setFormData({
        name: "",
        code: "",
        description: "",
        density_kg_per_liter: 0.92,
        minimum_stock_liters: undefined,
      });
    }
  }, [oilType, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.code.trim()) {
      toast({
        title: "Error",
        description: "Nombre y código son requeridos",
        variant: "destructive",
      });
      return;
    }

    if (isEditing && oilType) {
      updateMutation.mutate(
        { id: oilType.id, data: formData },
        {
          onSuccess: () => {
            toast({
              title: "Aceite actualizado",
              description: `${formData.name} ha sido actualizado`,
            });
            onOpenChange(false);
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          toast({
            title: "Aceite creado",
            description: `${formData.name} ha sido creado`,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Tipo de Aceite" : "Nuevo Tipo de Aceite"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifica los datos del tipo de aceite"
              : "Ingresa los datos del nuevo tipo de aceite"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">
                Código *
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="OLV, CAN, MAR..."
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nombre *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="col-span-3"
                placeholder="Aceite de Oliva Extra Virgen"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="density" className="text-right">
                Densidad
              </Label>
              <Input
                id="density"
                type="number"
                step="0.001"
                min="0.5"
                max="1.5"
                value={formData.density_kg_per_liter}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    density_kg_per_liter: parseFloat(e.target.value) || 0.92,
                  })
                }
                className="col-span-3"
              />
              <span className="col-span-4 -mt-2 text-right text-xs text-muted-foreground">
                kg/L (ej: 0.916 para oliva)
              </span>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="minimum_stock" className="text-right">
                Stock Mín.
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Input
                  id="minimum_stock"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.minimum_stock_liters ?? ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minimum_stock_liters: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    })
                  }
                  placeholder="Cantidad"
                />
                <span className="whitespace-nowrap text-sm text-muted-foreground">
                  toneladas
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Descripción
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="col-span-3"
                rows={2}
                placeholder="Descripción opcional..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
